import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import {
  Save,
  X,
  CheckCircle2,
  Clock,
  Edit2,
  Eye,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { getAllStudentActivityGrades, updateSubmissionGrade } from '../../utils/supabase/analytics';
import { isAutoGradable } from '../../utils/grading/autoGradeConfig';

interface GradingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  studentName: string;
  sessionId: string;
  onGradeUpdated?: () => void;
}

interface ActivitySubmission {
  id: string | null;
  slideId: string;
  slideTitle: string;
  slideType: string;
  slideContent: any;
  score: number | null;
  maxScore: number;
  graded: boolean;
  autoGraded: boolean;
  answer: any;
  createdAt: string | null;
}

export function GradingModal({
  open,
  onOpenChange,
  studentName,
  sessionId,
  onGradeUpdated
}: GradingModalProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activities, setActivities] = useState<ActivitySubmission[]>([]);
  
  // Ensure activities is always an array for safe rendering
  const activitiesArray = Array.isArray(activities) 
    ? activities 
    : (typeof activities === 'object' && activities !== null 
        ? Object.values(activities) 
        : []) as ActivitySubmission[];
  const [editingActivity, setEditingActivity] = useState<string | null>(null);
  const [tempScore, setTempScore] = useState<number>(0);
  const [tempFeedback, setTempFeedback] = useState<string>('');

  useEffect(() => {
    if (open && studentName && sessionId) {
      loadActivities();
    }
  }, [open, studentName, sessionId]);

  const loadActivities = async () => {
    setLoading(true);
    try {
      const allActivityData = await getAllStudentActivityGrades(sessionId, studentName);
      
      // Ensure allActivityData is always an array (convert objects to arrays if needed)
      const activitiesArray = Array.isArray(allActivityData) ? allActivityData : Object.values(allActivityData || {});
      
      // Deep normalize: recursively convert objects with numeric keys to arrays
      const deepNormalize = (value: any): any => {
        if (value === null || value === undefined) return value;
        if (typeof value !== 'object') return value;
        if (Array.isArray(value)) return value.map(deepNormalize);
        
        const keys = Object.keys(value);
        // Check if all keys are numeric (looks like a serialized array)
        if (keys.length > 0 && keys.every(k => /^\d+$/.test(k))) {
          return Object.values(value).map(deepNormalize);
        }
        
        // Recursively normalize object values
        const normalized: Record<string, any> = {};
        for (const [k, v] of Object.entries(value)) {
          normalized[k] = deepNormalize(v);
        }
        return normalized;
      };
      
      // Format all activities (both submitted and unsubmitted)
      const formattedActivities: ActivitySubmission[] = activitiesArray.map((sub: any) => {
        const slide = sub.slides;
        const autoGraded = isAutoGradable(slide?.type);
        
        // Deep normalize the answer to convert all nested objects with numeric keys to arrays
        const normalizedAnswer = deepNormalize(sub.answer);
        
        return {
          id: sub.id,
          slideId: slide?.id || '',
          slideTitle: slide?.content?.title || 'Untitled Activity',
          slideType: slide?.type || 'unknown',
          slideContent: slide?.content || {},
          score: sub.score,
          maxScore: slide?.content?.points || slide?.content?.maxPoints || (slide?.type === 'quiz' ? 100 : slide?.type === 'time_to_climb' ? 50 : 10),
          graded: sub.graded,
          autoGraded: autoGraded,
          answer: normalizedAnswer,
          createdAt: sub.created_at
        };
      });

      // Final validation: ensure formattedActivities is an array before setting state
      const validActivities = Array.isArray(formattedActivities) ? formattedActivities : Object.values(formattedActivities || {});
      console.log('[GradingModal] Loading', validActivities.length, 'activities (type:', Array.isArray(validActivities) ? 'array' : typeof validActivities, ')');
      setActivities(validActivities as ActivitySubmission[]);
    } catch (error) {
      console.error('Error loading activities:', error);
      toast.error('Failed to load student activities');
    } finally {
      setLoading(false);
    }
  };

  const handleEditActivity = (activity: ActivitySubmission) => {
    setEditingActivity(activity.id);
    setTempScore(activity.score || 0);
    setTempFeedback(activity.answer?.feedback || '');
  };

  const handleSaveGrade = async (activityId: string, maxScore: number) => {
    if (tempScore < 0 || tempScore > maxScore) {
      toast.error(`Score must be between 0 and ${maxScore}`);
      return;
    }

    setSaving(true);
    try {
      await updateSubmissionGrade(activityId, tempScore, tempFeedback);
      toast.success('Grade saved successfully');
      setEditingActivity(null);
      await loadActivities();
      onGradeUpdated?.();
    } catch (error) {
      console.error('Error saving grade:', error);
      toast.error('Failed to save grade');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingActivity(null);
    setTempScore(0);
    setTempFeedback('');
  };

  const getScoreColor = (score: number, maxScore: number) => {
    const percentage = (score / maxScore) * 100;
    if (percentage >= 90) return 'text-green-600';
    if (percentage >= 80) return 'text-emerald-600';
    if (percentage >= 70) return 'text-orange-600';
    if (percentage >= 60) return 'text-amber-600';
    return 'text-red-600';
  };

  const getActivityTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      multiple_choice: 'Multiple Choice',
      true_false: 'True/False',
      poll: 'Poll',
      open_ended: 'Open Ended',
      matching_pairs: 'Matching Pairs',
      time_to_climb: 'Time to Climb',
      draw_it: 'Draw It',
      collaborate_board: 'Collaborate Board',
      word_cloud: 'Word Cloud',
      fill_blanks: 'Fill in the Blanks',
      quiz: 'Quiz',
      drag_drop: 'Drag & Drop',
      sequencing: 'Sequencing',
      label_diagram: 'Label Diagram',
    };
    return labels[type] || type;
  };

  const renderAnswerPreview = (activity: ActivitySubmission) => {
    // Helper to normalize objects with numeric keys to arrays
    const normalizeToArray = (value: any): any => {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        const keys = Object.keys(value);
        if (keys.length > 0 && keys.every(k => /^\d+$/.test(k))) {
          return Object.values(value);
        }
      }
      return value;
    };

    // Helper to safely render any value as a React child (prevents object rendering errors)
    const safeRender = (value: any, fallback: string = 'N/A'): string => {
      if (value === null || value === undefined) return fallback;
      if (typeof value === 'string') return value;
      if (typeof value === 'number' || typeof value === 'boolean') return String(value);
      if (Array.isArray(value)) return value.map(v => safeRender(v, '')).join(', ');
      if (typeof value === 'object') {
        // Check if it looks like an array-like object
        const keys = Object.keys(value);
        if (keys.length > 0 && keys.every(k => /^\d+$/.test(k))) {
          return Object.values(value).map(v => safeRender(v, '')).join(', ');
        }
        return JSON.stringify(value);
      }
      return String(value);
    };

    // Helper: resolve a numeric option index to the actual option text from slide content
    const resolveOptionText = (index: any, content: any): string => {
      if (index === null || index === undefined) return 'N/A';
      const options = content?.options || content?.choices || [];
      if (Array.isArray(options) && typeof index === 'number' && index >= 0 && index < options.length) {
        const opt = options[index];
        // Options can be strings or objects with text/label
        if (typeof opt === 'string') return opt;
        if (opt?.text) return opt.text;
        if (opt?.label) return opt.label;
      }
      // Fallback: show the raw value
      return String(index);
    };

    switch (activity.slideType) {
      case 'multiple_choice': {
        // answer structure: { selectedOption: number, answer: number }
        const answer = activity.answer;
        const content = activity.slideContent;

        // Single-question: selectedOption is a numeric index
        const selectedIdx = answer?.selectedOption ?? answer?.answer;
        const optionLabel = resolveOptionText(selectedIdx, content);
        const mcCorrectIdx = content?.correctAnswer;
        const mcIsCorrect = selectedIdx !== null && selectedIdx !== undefined && mcCorrectIdx !== undefined
          ? selectedIdx === mcCorrectIdx
          : undefined;
        return (
          <div className="bg-blue-50/50 border border-blue-200/50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Eye className="w-4 h-4 text-blue-600" />
              <p className="text-sm font-semibold text-blue-900">Student Submission</p>
            </div>
            <div className="bg-white rounded p-3 border border-blue-100">
              <p className="text-xs font-medium text-muted-foreground mb-1">Selected Answer:</p>
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium">{optionLabel}</p>
                {mcIsCorrect !== undefined && (
                  <span className={`text-xs ${mcIsCorrect ? 'text-green-600' : 'text-red-600'}`}>
                    {mcIsCorrect ? '✓ Correct' : `✗ Incorrect (correct: ${resolveOptionText(mcCorrectIdx, content)})`}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      }

      case 'quiz': {
        // Quiz is always multi-question: { answers: Record<number, number> }
        // Legacy buggy submissions may have: { selectedOption: Record, answer: Record }
        const quizAnswer = activity.answer;
        const quizContent = activity.slideContent;
        const quizQuestions: any[] = quizContent?.questions || [];

        // Resolve the answers map — handle both correct and legacy formats
        let quizAnswersMap: Record<number, number> = {};
        if (quizAnswer?.answers && typeof quizAnswer.answers === 'object') {
          quizAnswersMap = quizAnswer.answers;
        } else if (quizAnswer?.selectedOption && typeof quizAnswer.selectedOption === 'object') {
          // Legacy: { selectedOption: Record<number, number> }
          quizAnswersMap = quizAnswer.selectedOption;
        } else if (quizAnswer?.answer && typeof quizAnswer.answer === 'object' && !Array.isArray(quizAnswer.answer)) {
          quizAnswersMap = quizAnswer.answer;
        }

        const quizAnsweredCount = Object.keys(quizAnswersMap).length;

        if (quizQuestions.length > 0) {
          let quizCorrectTotal = 0;
          return (
            <div className="bg-blue-50/50 border border-blue-200/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Eye className="w-4 h-4 text-blue-600" />
                <p className="text-sm font-semibold text-blue-900">Student Submission</p>
              </div>
              <div className="bg-white rounded p-3 border border-blue-100 space-y-2">
                <p className="text-xs font-medium text-muted-foreground mb-1">
                  Answers ({quizAnsweredCount}/{quizQuestions.length} answered):
                </p>
                {quizQuestions.map((q: any, idx: number) => {
                  const studentAnswer = quizAnswersMap[idx];
                  const optionText = resolveOptionText(studentAnswer, q);
                  const qCorrectIdx = q.correctAnswer;
                  const qIsCorrect = studentAnswer !== undefined && studentAnswer !== null && qCorrectIdx !== undefined
                    ? studentAnswer === qCorrectIdx
                    : undefined;
                  if (qIsCorrect === true) quizCorrectTotal++;
                  return (
                    <div key={idx} className="text-sm min-w-0">
                      <div className="flex items-start gap-1 min-w-0">
                        <span className="font-medium min-w-0 break-words whitespace-normal">Q{idx + 1}{q.question ? `: ${q.question}` : ''}:</span>
                      </div>
                      <div className="flex flex-wrap items-start gap-x-2 gap-y-1 ml-4 min-w-0">
                        <span className="min-w-0 break-words whitespace-normal">{studentAnswer !== undefined && studentAnswer !== null ? optionText : 'Not answered'}</span>
                        {qIsCorrect !== undefined && (
                          <span className={`text-xs shrink-0 ${qIsCorrect ? 'text-green-600' : 'text-red-600'}`}>
                            {qIsCorrect ? '✓' : `✗ (correct: ${resolveOptionText(qCorrectIdx, q)})`}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }

        // Fallback if no questions in content
        return (
          <div className="bg-blue-50/50 border border-blue-200/50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Eye className="w-4 h-4 text-blue-600" />
              <p className="text-sm font-semibold text-blue-900">Student Submission</p>
            </div>
            <div className="bg-white rounded p-3 border border-blue-100">
              <p className="text-sm text-muted-foreground">Quiz answers submitted</p>
              {quizAnsweredCount > 0 && (
                <details className="mt-2">
                  <summary className="text-xs text-blue-600 cursor-pointer hover:underline">View raw data</summary>
                  <pre className="mt-2 text-xs bg-muted p-2 rounded overflow-auto max-h-40">
                    {JSON.stringify(quizAnswersMap, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          </div>
        );
      }
      
      case 'open_ended':
        return (
          <div className="bg-blue-50/50 border border-blue-200/50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Eye className="w-4 h-4 text-blue-600" />
              <p className="text-sm font-semibold text-blue-900">Student Submission</p>
            </div>
            <div className="bg-white rounded p-3 border border-blue-100">
              <p className="text-xs font-medium text-muted-foreground mb-2">Written Response:</p>
              <p className="text-sm whitespace-pre-wrap">{safeRender(activity.answer?.answer || activity.answer?.response || activity.answer?.text, 'No response submitted')}</p>
            </div>
          </div>
        );
      
      case 'draw_it': {
        // answer structure: { drawing: "data:image/png;base64,..." }
        const drawingData = activity.answer?.drawing || (typeof activity.answer === 'string' && activity.answer.startsWith('data:') ? activity.answer : null);
        return (
          <div className="bg-blue-50/50 border border-blue-200/50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Eye className="w-4 h-4 text-blue-600" />
              <p className="text-sm font-semibold text-blue-900">Student Submission</p>
            </div>
            <div className="bg-white rounded p-3 border border-blue-100">
              <p className="text-xs font-medium text-muted-foreground mb-2">Drawing:</p>
              {drawingData ? (
                <img 
                  src={drawingData} 
                  alt="Student drawing" 
                  className="max-w-full rounded border border-border shadow-sm"
                />
              ) : (
                <p className="text-sm text-muted-foreground italic">No drawing submitted</p>
              )}
            </div>
          </div>
        );
      }
      
      case 'true_false': {
        // answer structure: { answer: boolean }
        const tfAnswer = activity.answer?.answer;
        const tfContent = activity.slideContent;
        const tfCorrectAnswer = tfContent?.correctAnswer;
        const tfHasAnswer = tfAnswer !== undefined && tfAnswer !== null;
        const tfDisplayText = tfHasAnswer ? (tfAnswer ? 'True' : 'False') : 'N/A';
        const tfIsCorrect = tfHasAnswer && tfCorrectAnswer !== undefined ? tfAnswer === tfCorrectAnswer : undefined;
        return (
          <div className="bg-blue-50/50 border border-blue-200/50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Eye className="w-4 h-4 text-blue-600" />
              <p className="text-sm font-semibold text-blue-900">Student Submission</p>
            </div>
            <div className="bg-white rounded p-3 border border-blue-100">
              <p className="text-xs font-medium text-muted-foreground mb-1">Selected Answer:</p>
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium">{tfDisplayText}</p>
                {tfIsCorrect !== undefined && (
                  <span className={`text-xs ${tfIsCorrect ? 'text-green-600' : 'text-red-600'}`}>
                    {tfIsCorrect ? '✓ Correct' : `✗ Incorrect (answer: ${tfCorrectAnswer ? 'True' : 'False'})`}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      }
      
      case 'matching_pairs': {
        // answer structure: { matches: Record<string, string>, correctCount, total }
        // matches is a flat object: { leftId: rightId }
        const matchesData = activity.answer?.matches;
        const content = activity.slideContent;
        // Build lookup maps from slide content for readable labels
        const pairs = content?.pairs;
        const leftItems = content?.leftItems;
        const rightItems = content?.rightItems;

        const getLeftLabel = (leftId: string): string => {
          if (leftItems && Array.isArray(leftItems)) {
            const item = leftItems.find((li: any) => (li.id ?? `left-${leftItems.indexOf(li)}`) === leftId);
            if (item) return item.text || item.label || leftId;
          }
          if (pairs && Array.isArray(pairs)) {
            const idx = parseInt(leftId.replace('left-', ''), 10);
            if (!isNaN(idx) && pairs[idx]) return pairs[idx].left || pairs[idx].prompt || leftId;
          }
          return leftId;
        };

        const getRightLabel = (rightId: string): string => {
          if (rightItems && Array.isArray(rightItems)) {
            const item = rightItems.find((ri: any) => (ri.id ?? `right-${rightItems.indexOf(ri)}`) === rightId);
            if (item) return item.text || item.label || rightId;
          }
          if (pairs && Array.isArray(pairs)) {
            const idx = parseInt(rightId.replace('right-', ''), 10);
            if (!isNaN(idx) && pairs[idx]) return pairs[idx].right || pairs[idx].answer || rightId;
          }
          return rightId;
        };

        // Determine correct match for a left ID (left-N → right-N)
        const getCorrectRightId = (leftId: string): string => {
          const idx = parseInt(leftId.replace('left-', ''), 10);
          return `right-${idx}`;
        };

        return (
          <div className="space-y-3">
            <div className="bg-blue-50/50 border border-blue-200/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Eye className="w-4 h-4 text-blue-600" />
                <p className="text-sm font-semibold text-blue-900">Student Submission</p>
              </div>
              <div className="bg-white rounded p-3 border border-blue-100">
                <p className="text-xs font-medium text-muted-foreground mb-2">Matched Pairs:</p>
                {matchesData && typeof matchesData === 'object' && Object.keys(matchesData).length > 0 ? (
                  <div className="space-y-1">
                    {Object.entries(matchesData).map(([leftId, rightId]: [string, any]) => {
                      const correctRightId = getCorrectRightId(leftId);
                      const isCorrectMatch = String(rightId) === correctRightId;
                      return (
                        <div key={leftId} className="text-sm flex items-center gap-2">
                          <span className="font-medium">{getLeftLabel(leftId)}</span>
                          <span className="text-muted-foreground">→</span>
                          <span>{getRightLabel(String(rightId))}</span>
                          <span className={`text-xs ${isCorrectMatch ? 'text-green-600' : 'text-red-600'}`}>
                            {isCorrectMatch ? '✓' : `✗ (correct: ${getRightLabel(correctRightId)})`}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground italic">No matches submitted</p>
                )}
                {activity.answer?.correctCount !== undefined && activity.answer?.total !== undefined && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Correct: {activity.answer.correctCount}/{activity.answer.total}
                  </p>
                )}
              </div>
            </div>
            {/* Correct Answer Key */}
            {Array.isArray(pairs) && pairs.length > 0 && (
              <div className="bg-green-50/50 border border-green-200/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  <p className="text-sm font-semibold text-green-900">Correct Answer</p>
                </div>
                <div className="bg-white rounded p-3 border border-green-100">
                  <div className="space-y-1">
                    {pairs.map((pair: any, idx: number) => (
                      <div key={idx} className="text-sm flex items-center gap-2">
                        <span className="font-medium">{pair.left}</span>
                        <span className="text-muted-foreground">→</span>
                        <span>{pair.right}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      }
      
      case 'fill_blanks': {
        // answer structure: { answers: Record<number, string> } or after deepNormalize may be an array
        const fbAnswers = activity.answer?.answers;
        const fbContent = activity.slideContent;
        const fbCorrectAnswers = fbContent?.answers || [];
        const fbSentence = fbContent?.sentence || '';

        // Convert to entries: [[index, value], ...] — handle both object and array
        let fbEntries: [string, any][] = [];
        if (fbAnswers && typeof fbAnswers === 'object') {
          fbEntries = Object.entries(fbAnswers);
        }

        return (
          <div className="bg-blue-50/50 border border-blue-200/50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Eye className="w-4 h-4 text-blue-600" />
              <p className="text-sm font-semibold text-blue-900">Student Submission</p>
            </div>
            <div className="bg-white rounded p-3 border border-blue-100">
              {fbSentence && (
                <div className="mb-3">
                  <p className="text-xs font-medium text-muted-foreground mb-1">Sentence:</p>
                  <p className="text-sm italic text-muted-foreground">{fbSentence}</p>
                </div>
              )}
              <p className="text-xs font-medium text-muted-foreground mb-2">Filled Answers:</p>
              {fbEntries.length > 0 ? (
                <div className="space-y-1">
                  {fbEntries.map(([key, value]: [string, any]) => {
                    const idx = parseInt(key, 10);
                    const correctAnswer = !isNaN(idx) ? fbCorrectAnswers[idx] : undefined;
                    const studentStr = String(value || '').trim().toLowerCase();
                    const correctStr = correctAnswer ? String(correctAnswer).trim().toLowerCase() : undefined;
                    const isCorrect = correctStr !== undefined && studentStr === correctStr;
                    const showResult = correctStr !== undefined;

                    return (
                      <div key={key} className="text-sm flex items-center gap-2">
                        <span className="font-medium">Blank {(!isNaN(idx) ? idx + 1 : key)}:</span>
                        <span>{safeRender(value)}</span>
                        {showResult && (
                          <span className={`text-xs ${isCorrect ? 'text-green-600' : 'text-red-600'}`}>
                            {isCorrect ? '✓' : `✗ (correct: ${correctAnswer})`}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground italic">No answers submitted</p>
              )}
            </div>
          </div>
        );
      }
      
      case 'time_to_climb': {
        // answer structure: { answers: Record<number, number | string> }
        const ttcAnswers = activity.answer?.answers;
        const ttcContent = activity.slideContent;
        const ttcQuestions = ttcContent?.questions || [];
        const ttcQuizType = ttcContent?.quizType || 'multiple_choice';

        return (
          <div className="bg-blue-50/50 border border-blue-200/50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Eye className="w-4 h-4 text-blue-600" />
              <p className="text-sm font-semibold text-blue-900">Student Submission</p>
            </div>
            <div className="bg-white rounded p-3 border border-blue-100">
              <p className="text-xs font-medium text-muted-foreground mb-2">Activity Results:</p>
              {ttcAnswers && typeof ttcAnswers === 'object' && ttcQuestions.length > 0 ? (
                <div className="space-y-2 text-sm">
                  {ttcQuestions.map((q: any, idx: number) => {
                    const studentAnswer = ttcAnswers[idx];
                    let displayAnswer: string;
                    if (ttcQuizType === 'identification') {
                      displayAnswer = studentAnswer !== undefined && studentAnswer !== null ? String(studentAnswer) : 'No answer';
                    } else {
                      displayAnswer = resolveOptionText(studentAnswer, q);
                    }
                    return (
                      <div key={idx}>
                        <span className="font-medium">Q{idx + 1}{q.question ? `: ${q.question}` : ''}</span>
                        <br />
                        <span className="ml-2">Answer: {displayAnswer}</span>
                      </div>
                    );
                  })}
                </div>
              ) : ttcAnswers && typeof ttcAnswers === 'object' ? (
                <div className="space-y-1 text-sm">
                  {Object.entries(ttcAnswers).map(([qIdx, ans]: [string, any]) => (
                    <div key={qIdx}>
                      <span className="font-medium">Q{Number(qIdx) + 1}:</span> {safeRender(ans)}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground italic">No answers submitted</p>
              )}
            </div>
          </div>
        );
      }
      
      case 'word_cloud': {
        // answer structure: { words: string[] } or possibly { words: string } for single word
        const rawWords = activity.answer?.words;
        let wordsList: string[] = [];
        if (Array.isArray(rawWords)) {
          wordsList = rawWords;
        } else if (rawWords && typeof rawWords === 'object') {
          // deepNormalize may not have caught it; try Object.values
          wordsList = Object.values(rawWords);
        } else if (typeof rawWords === 'string' && rawWords.trim()) {
          wordsList = [rawWords];
        }

        return (
          <div className="bg-blue-50/50 border border-blue-200/50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Eye className="w-4 h-4 text-blue-600" />
              <p className="text-sm font-semibold text-blue-900">Student Submission</p>
            </div>
            <div className="bg-white rounded p-3 border border-blue-100">
              <p className="text-xs font-medium text-muted-foreground mb-2">Submitted Words/Phrases:</p>
              {wordsList.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {wordsList.map((word: string, idx: number) => (
                    <Badge key={idx} variant="secondary" className="text-sm">{safeRender(word)}</Badge>
                  ))}
                </div>
              ) : activity.answer?.text ? (
                <p className="text-sm">{safeRender(activity.answer.text)}</p>
              ) : (
                <p className="text-sm text-muted-foreground italic">No words submitted</p>
              )}
            </div>
          </div>
        );
      }
      
      case 'collaborate_board': {
        // answer structure: { notes: StickyNote[] } where each note has { id, content, author, position, color }
        const notes = activity.answer?.notes;
        const hasNotes = Array.isArray(notes) && notes.length > 0;
        return (
          <div className="bg-blue-50/50 border border-blue-200/50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Eye className="w-4 h-4 text-blue-600" />
              <p className="text-sm font-semibold text-blue-900">Student Submission</p>
            </div>
            <div className="bg-white rounded p-3 border border-blue-100">
              <p className="text-xs font-medium text-muted-foreground mb-2">Contribution:</p>
              {hasNotes ? (
                <div className="space-y-2">
                  {notes.map((note: any, idx: number) => (
                    <div key={note.id || idx} className="text-sm bg-yellow-50 border border-yellow-200 rounded p-2">
                      <span className="whitespace-pre-wrap">{safeRender(note.content || note.text, 'Empty note')}</span>
                    </div>
                  ))}
                </div>
              ) : activity.answer?.content ? (
                <div className="text-sm whitespace-pre-wrap">{safeRender(activity.answer.content)}</div>
              ) : activity.answer?.drawing ? (
                <img 
                  src={activity.answer.drawing} 
                  alt="Student contribution" 
                  className="max-w-full rounded border border-border shadow-sm"
                />
              ) : activity.answer && typeof activity.answer === 'object' && Object.keys(activity.answer).length > 0 ? (
                <details>
                  <summary className="text-xs text-blue-600 cursor-pointer hover:underline">View submission data</summary>
                  <pre className="mt-2 text-xs bg-muted p-2 rounded overflow-auto max-h-40">
                    {JSON.stringify(activity.answer, null, 2)}
                  </pre>
                </details>
              ) : (
                <p className="text-sm text-muted-foreground italic">No contribution submitted</p>
              )}
            </div>
          </div>
        );
      }
      
      case 'poll': {
        // answer structure: { selectedOption: number } — index into options array
        const pollAnswer = activity.answer;
        const pollContent = activity.slideContent;
        const pollSelectedIdx = pollAnswer?.selectedOption ?? pollAnswer?.option;
        const pollOptionText = resolveOptionText(pollSelectedIdx, pollContent);
        return (
          <div className="bg-blue-50/50 border border-blue-200/50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Eye className="w-4 h-4 text-blue-600" />
              <p className="text-sm font-semibold text-blue-900">Student Submission</p>
            </div>
            <div className="bg-white rounded p-3 border border-blue-100">
              <p className="text-xs font-medium text-muted-foreground mb-1">Selected Option:</p>
              <p className="text-sm font-medium">{pollOptionText}</p>
            </div>
          </div>
        );
      }
      
      case 'drag_drop': {
        // answer structure: { [itemId: string]: targetId } — flat object
        // OR legacy: { answer: { [itemId]: targetId } } from old default handler
        const ddRawAnswer = activity.answer;
        const ddContent = activity.slideContent;
        const ddItems = ddContent?.items || [];
        const ddTargets = ddContent?.targets || ddContent?.zones || [];

        // Resolve the actual placements object, handling both formats
        let ddPlacements: Record<string, any> = {};
        if (ddRawAnswer && typeof ddRawAnswer === 'object') {
          if (ddRawAnswer.answer && typeof ddRawAnswer.answer === 'object' && !Array.isArray(ddRawAnswer.answer)) {
            // Legacy wrapped format: { answer: { item1: target1, ... } }
            ddPlacements = ddRawAnswer.answer;
          } else {
            // Correct flat format: { item1: target1, ... }
            ddPlacements = ddRawAnswer;
          }
        }

        // Strip embedded " → Category" or " -> Category" from item labels
        // (legacy data may have stored the full "Dog → Animals" as the label)
        const cleanItemLabel = (raw: string): string => {
          let sep = raw.lastIndexOf(' → ');
          if (sep < 0) sep = raw.lastIndexOf(' -> ');
          return sep >= 0 ? raw.slice(0, sep).trim() : raw;
        };

        const getItemLabel = (itemId: string): string => {
          if (Array.isArray(ddItems)) {
            const item = ddItems.find((i: any) => i.id === itemId);
            if (item) return cleanItemLabel(item.text || item.label || itemId);
          }
          return itemId;
        };

        const getTargetLabel = (targetId: string): string => {
          if (Array.isArray(ddTargets)) {
            const target = ddTargets.find((t: any) => t.id === targetId);
            if (target) return target.text || target.label || targetId;
          }
          return targetId;
        };

        // Resolve correct target for an item — use targetId if set,
        // otherwise try to extract category from embedded label like "Dog → Animals"
        const resolveCorrectTarget = (item: any): string => {
          if (item.targetId) return item.targetId;
          const raw = item.text || item.label || '';
          let sep = raw.lastIndexOf(' → ');
          let sepLen = 3;
          if (sep < 0) { sep = raw.lastIndexOf(' -> '); sepLen = 4; }
          if (sep >= 0) {
            const embeddedCategory = raw.slice(sep + sepLen).trim();
            const matchedTarget = ddTargets.find((t: any) => (t.text || t.label) === embeddedCategory);
            if (matchedTarget) return matchedTarget.id;
          }
          return '';
        };

        const ddEntries = ddPlacements && typeof ddPlacements === 'object'
          ? Object.entries(ddPlacements).filter(([k]) => k !== 'feedback' && k !== 'answer')
          : [];

        return (
          <div className="space-y-3">
            <div className="bg-blue-50/50 border border-blue-200/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Eye className="w-4 h-4 text-blue-600" />
                <p className="text-sm font-semibold text-blue-900">Student Submission</p>
              </div>
              <div className="bg-white rounded p-3 border border-blue-100">
                <p className="text-xs font-medium text-muted-foreground mb-2">Placements:</p>
                {ddEntries.length > 0 ? (
                  <div className="space-y-1">
                    {ddEntries.map(([itemId, targetId]: [string, any]) => {
                      const ddItem = ddItems.find((i: any) => i.id === itemId);
                      const expectedTarget = ddItem ? resolveCorrectTarget(ddItem) : '';
                      const hasExpectedTarget = expectedTarget !== '';
                      const isCorrectPlacement = hasExpectedTarget && expectedTarget === targetId;
                      return (
                        <div key={itemId} className="text-sm flex items-center gap-2">
                          <span className="font-medium">{getItemLabel(itemId)}</span>
                          <span className="text-muted-foreground">→</span>
                          <span>{getTargetLabel(String(targetId))}</span>
                          {hasExpectedTarget && (
                            <span className={`text-xs ${isCorrectPlacement ? 'text-green-600' : 'text-red-600'}`}>
                              {isCorrectPlacement ? '✓' : `✗ (correct: ${getTargetLabel(expectedTarget)})`}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground italic">No placements submitted</p>
                )}
              </div>
            </div>
            {/* Correct Answer Key */}
            {Array.isArray(ddItems) && ddItems.length > 0 && (
              <div className="bg-green-50/50 border border-green-200/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  <p className="text-sm font-semibold text-green-900">Correct Answer</p>
                </div>
                <div className="bg-white rounded p-3 border border-green-100">
                  <div className="space-y-1">
                    {ddItems.map((item: any) => {
                      const resolvedTarget = resolveCorrectTarget(item);
                      return (
                        <div key={item.id} className="text-sm flex items-center gap-2">
                          <span className="font-medium">{cleanItemLabel(item.text || item.label || item.id)}</span>
                          <span className="text-muted-foreground">→</span>
                          <span>{resolvedTarget ? getTargetLabel(resolvedTarget) : <span className="text-muted-foreground italic">(not assigned)</span>}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      }

      case 'sequencing': {
        // answer structure: { order: string[] } — array of item IDs in student's order
        // OR legacy: { answer: [...] } from old default handler
        let seqOrder = activity.answer?.order;
        if (!seqOrder && Array.isArray(activity.answer?.answer)) {
          seqOrder = activity.answer.answer; // Legacy wrapped format
        }
        if (!seqOrder && Array.isArray(activity.answer)) {
          seqOrder = activity.answer; // Direct array format
        }
        const seqContent = activity.slideContent;
        const seqItems = seqContent?.items || [];
        const seqCorrectOrder = seqContent?.correctOrder || [];

        const getSeqItemLabel = (itemId: string): string => {
          if (Array.isArray(seqItems)) {
            const item = seqItems.find((i: any) => i.id === itemId);
            if (item) return item.text || item.label || itemId;
          }
          return itemId;
        };

        return (
          <div className="space-y-3">
            <div className="bg-blue-50/50 border border-blue-200/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Eye className="w-4 h-4 text-blue-600" />
                <p className="text-sm font-semibold text-blue-900">Student Submission</p>
              </div>
              <div className="bg-white rounded p-3 border border-blue-100">
                <p className="text-xs font-medium text-muted-foreground mb-2">Student's Order:</p>
                {Array.isArray(seqOrder) && seqOrder.length > 0 ? (
                  <div className="space-y-1">
                    {seqOrder.map((itemId: string, idx: number) => {
                      const isPositionCorrect = seqCorrectOrder.length > 0 && seqCorrectOrder[idx] === itemId;
                      return (
                        <div key={idx} className="text-sm flex items-center gap-2">
                          <span className="font-medium text-muted-foreground w-6">{idx + 1}.</span>
                          <span>{getSeqItemLabel(itemId)}</span>
                          {seqCorrectOrder.length > 0 && (
                            <span className={`text-xs ${isPositionCorrect ? 'text-green-600' : 'text-red-600'}`}>
                              {isPositionCorrect ? '✓' : `✗ (should be: ${getSeqItemLabel(seqCorrectOrder[idx])})`}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground italic">No sequence submitted</p>
                )}
              </div>
            </div>
            {/* Correct Answer Key */}
            {seqCorrectOrder.length > 0 && (
              <div className="bg-green-50/50 border border-green-200/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  <p className="text-sm font-semibold text-green-900">Correct Order</p>
                </div>
                <div className="bg-white rounded p-3 border border-green-100">
                  <div className="space-y-1">
                    {seqCorrectOrder.map((itemId: string, idx: number) => (
                      <div key={idx} className="text-sm flex items-center gap-2">
                        <span className="font-medium text-muted-foreground w-6">{idx + 1}.</span>
                        <span>{getSeqItemLabel(itemId)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      }

      case 'label_diagram': {
        // answer structure: { placements: Array<{labelId, position: {x, y}}> }
        const ldPlacements = activity.answer?.placements;
        const ldContent = activity.slideContent;
        const ldLabels = ldContent?.labels || [];

        const getLabelText = (labelId: string): string => {
          if (Array.isArray(ldLabels)) {
            const label = ldLabels.find((l: any) => l.id === labelId);
            if (label) return label.text || label.label || labelId;
          }
          return labelId;
        };

        return (
          <div className="bg-blue-50/50 border border-blue-200/50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Eye className="w-4 h-4 text-blue-600" />
              <p className="text-sm font-semibold text-blue-900">Student Submission</p>
            </div>
            <div className="bg-white rounded p-3 border border-blue-100">
              <p className="text-xs font-medium text-muted-foreground mb-2">Label Placements:</p>
              {Array.isArray(ldPlacements) && ldPlacements.length > 0 ? (
                <div className="space-y-1">
                  {ldPlacements.map((placement: any, idx: number) => (
                    <div key={idx} className="text-sm">
                      <span className="font-medium">{getLabelText(placement.labelId)}</span>
                      <span className="text-muted-foreground ml-2">
                        (x: {Math.round(placement.position?.x || 0)}, y: {Math.round(placement.position?.y || 0)})
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground italic">No labels placed</p>
              )}
            </div>
          </div>
        );
      }

      default:
        return (
          <div className="bg-blue-50/50 border border-blue-200/50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Eye className="w-4 h-4 text-blue-600" />
              <p className="text-sm font-semibold text-blue-900">Student Submission</p>
            </div>
            <div className="bg-white rounded p-3 border border-blue-100">
              <p className="text-sm text-muted-foreground">Submission data available for review</p>
              {activity.answer && (
                <details className="mt-2">
                  <summary className="text-xs text-blue-600 cursor-pointer hover:underline">View raw data</summary>
                  <pre className="mt-2 text-xs bg-muted p-2 rounded overflow-auto max-h-40">
                    {JSON.stringify(activity.answer, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          </div>
        );
    }
  };

  // Note: activitiesArray is defined at the top of component to ensure safe type access
  
  // Calculate stats from safe activities array
  const completedCount = activitiesArray?.filter?.(a => a?.graded)?.length ?? 0;
  const totalScore = activitiesArray?.reduce?.((sum, a) => {
    if (a?.graded && a?.score !== null) {
      // Cap each activity's score to its maxScore
      return sum + Math.min(a?.score ?? 0, a?.maxScore ?? 0);
    }
    return sum;
  }, 0) ?? 0;
  const maxPossibleScore = activitiesArray?.reduce?.((sum, a) => sum + (a?.maxScore ?? 0), 0) ?? 0;
  const percentage = maxPossibleScore > 0 ? (totalScore / maxPossibleScore) * 100 : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="text-2xl">Grade Student Activities</DialogTitle>
          <DialogDescription>
            Review student submissions and grade activities for <span className="font-semibold text-foreground">{studentName}</span>
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <>
            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-4 p-4 bg-muted/30 rounded-lg">
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-1">Graded</p>
                <p className="text-2xl font-bold">{completedCount}/{activitiesArray?.length ?? 0}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-1">Total Score</p>
                <p className="text-2xl font-bold">{totalScore}/{maxPossibleScore}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-1">Percentage</p>
                <p className={`text-2xl font-bold ${getScoreColor(totalScore, maxPossibleScore)}`}>
                  {percentage.toFixed(1)}%
                </p>
              </div>
            </div>

            {/* Activities List */}
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-4">
                {(() => {
                  // Use the pre-converted activitiesArray from component scope
                  const safActivityArray = activitiesArray ?? [];
                  
                  if (!safActivityArray || safActivityArray.length === 0) {
                    return (
                      <div className="text-center py-8">
                        <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-50" />
                        <p className="text-muted-foreground">No activities found</p>
                      </div>
                    );
                  }
                  
                  return safActivityArray.map((activity: ActivitySubmission, index: number) => (
                    <div
                      key={activity.id || `unsubmitted-${activity.slideId}-${index}`}
                      className="border border-border rounded-xl p-4 bg-card"
                    >
                      {/* Activity Header */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold">{activity.slideTitle}</h4>
                            <Badge variant="outline" className="text-xs">
                              {getActivityTypeLabel(activity.slideType)}
                            </Badge>
                            {activity.id && activity.autoGraded && (
                              <Badge className="text-xs bg-blue-100 text-blue-700 border-blue-200">
                                AUTO
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {activity.createdAt 
                              ? `Submitted ${new Date(activity.createdAt).toLocaleString()}`
                              : 'Not submitted'
                            }
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {!activity.id ? (
                            <Badge variant="secondary" className="bg-gray-100 text-gray-700">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Not Submitted
                            </Badge>
                          ) : activity.graded ? (
                            <div className="text-right">
                              <p className={`text-xl font-bold ${getScoreColor(Math.min(activity.score || 0, activity.maxScore), activity.maxScore)}`}>
                                {Math.min(activity.score || 0, activity.maxScore)}/{activity.maxScore}
                              </p>
                            </div>
                          ) : (
                            <Badge variant="secondary" className="bg-orange-100 text-orange-700">
                              <Clock className="w-3 h-3 mr-1" />
                              Pending
                            </Badge>
                          )}
                        </div>
                      </div>

                      <Separator className="mb-3" />

                      {/* Student Submission - Highlighted Section */}
                      <div className="mb-3">
                        {!activity.id ? (
                          <div className="bg-gray-50/50 border border-gray-200/50 rounded-lg p-4">
                            <div className="flex items-center gap-2">
                              <AlertCircle className="w-4 h-4 text-gray-600" />
                              <p className="text-sm text-gray-600 font-medium">This activity was not submitted by the student</p>
                            </div>
                          </div>
                        ) : (
                          renderAnswerPreview(activity)
                        )}
                      </div>

                      {/* Grading Section */}
                      {!activity.id ? (
                        <div className="border-t border-border pt-3">
                          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                            <p className="text-sm text-gray-700">
                              This activity was not submitted, so no grading is available.
                            </p>
                          </div>
                        </div>
                      ) : !activity.autoGraded && (
                        <div className="border-t border-border pt-3">
                          {editingActivity === activity.id ? (
                            <div className="space-y-3">
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <Label htmlFor={`score-${activity.id}`} className="text-sm">
                                    Score (0-{activity.maxScore})
                                  </Label>
                                  <Input
                                    id={`score-${activity.id}`}
                                    type="number"
                                    min="0"
                                    max={activity.maxScore}
                                    value={tempScore}
                                    onChange={(e) => setTempScore(Number(e.target.value))}
                                    className="mt-1"
                                    disabled={saving}
                                  />
                                </div>
                              </div>
                              <div>
                                <Label htmlFor={`feedback-${activity.id}`} className="text-sm">
                                  Feedback (Optional)
                                </Label>
                                <Textarea
                                  id={`feedback-${activity.id}`}
                                  value={tempFeedback}
                                  onChange={(e) => setTempFeedback(e.target.value)}
                                  placeholder="Provide feedback to the student..."
                                  className="mt-1 min-h-[80px]"
                                  disabled={saving}
                                />
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  onClick={() => handleSaveGrade(activity.id!, activity.maxScore)}
                                  disabled={saving}
                                  size="sm"
                                >
                                  {saving ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  ) : (
                                    <Save className="w-4 h-4 mr-2" />
                                  )}
                                  Save Grade
                                </Button>
                                <Button
                                  variant="outline"
                                  onClick={handleCancelEdit}
                                  disabled={saving}
                                  size="sm"
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              {activity.answer?.feedback && (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-2">
                                  <p className="text-sm font-semibold text-blue-900 mb-1">Teacher Feedback</p>
                                  <p className="text-sm text-blue-800">{typeof activity.answer?.feedback === 'string' ? activity.answer.feedback : JSON.stringify(activity.answer?.feedback)}</p>
                                </div>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditActivity(activity)}
                              >
                                <Edit2 className="w-4 h-4 mr-2" />
                                {activity.graded ? 'Update Grade' : 'Grade Activity'}
                              </Button>
                            </>
                          )}
                        </div>
                      )}

                      {/* Auto-graded with feedback */}
                      {activity.id && activity.autoGraded && activity.answer?.feedback && (
                        <div className="border-t border-border pt-3">
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <p className="text-sm font-semibold text-blue-900 mb-1">Teacher Feedback</p>
                            <p className="text-sm text-blue-800">{typeof activity.answer?.feedback === 'string' ? activity.answer.feedback : JSON.stringify(activity.answer?.feedback)}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ));
                })()}
              </div>
            </ScrollArea>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
